import _get from 'lodash-es/get';
import { isCreditCard, isLoan } from '@amount/frontend-product-utilities';
import { FetchResult } from 'apollo-link';

import { ITagInfo } from '../../customTypings/avant-config/tenantConfig';
import { client } from '../../apolloClient';

import GetTealiumApplicationInfoQuery, {
  FragmentSelectionOnCreditCardAccount,
  FragmentSelectionOnLoan,
  GetTealiumApplicationInfo,
  GetTealiumApplicationInfoInput,
  SelectionOnCustomer,
  SelectionOnCustomerApplication,
  SelectionOnLead,
  SelectionOnMoney
} from './queries/GetTealiumApplicationInfo.graphql';
import VerificationsAnalyticsEvent, {
  VerificationsAnalyticsEventInput
} from './queries/VerificationsAnalyticsEvent.graphql';
import Tealium from './common';

interface IGetAmountValueProps {
  amount: SelectionOnMoney | null;
  amountWithFees: SelectionOnMoney | null;
}

const getAmountValue: (props: IGetAmountValueProps) => number | null | undefined = ({ amount, amountWithFees }) =>
  amountWithFees?.amount || amount?.amount;

class AvantTealium extends Tealium {
  public async view (pageSpecificTags?: ITagInfo): Promise<void> {
    const data = await this.getApplicationData();
    const appData = this.getFormattedData(data);
    const isInVerification = !!data?.me?.getApplication?.isInVerification;

    this.sendServerSideAnalyticsEvent(appData, isInVerification, pageSpecificTags);

    super.view({
      event_action: 'view',
      event_category: 'verification',
      page_category: 'verification',
      tealium_event: 'verification_view',
      ...pageSpecificTags,
      ...appData
    });
  }

  public async sendEvent (eventTags?: ITagInfo): Promise<void> {
    const data = await this.getApplicationData();
    const appData = this.getFormattedData(data);

    super.sendEvent({
      event_action: 'submit',
      event_category: 'verification',
      page_category: 'verification',
      tealium_event: 'verification_view',
      ...eventTags,
      ...appData
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public sendClickEvent (): void {}

  public getApplicationData: () => Promise<GetTealiumApplicationInfo | null> = async () => {
    const appUuid = super.applicationUuid;
    if (!appUuid) {
      return null;
    }

    const { data } = await client.query<GetTealiumApplicationInfo, GetTealiumApplicationInfoInput>({
      query: GetTealiumApplicationInfoQuery,
      variables: {
        uuid: appUuid
      },
      fetchPolicy: 'cache-first'
    });

    return data;
  }

  public getFormattedData: (data: GetTealiumApplicationInfo | null) => ITagInfo = data => {
    if (!data) {
      return {};
    }

    return {
      ...this.formattedCustomerData(data.me),
      ...this.formattedCustomerApplicationData(data.me.getApplication),
      ...this.formattedProductData(data.me.getApplication && data.me.getApplication.product)
    };
  }

  private readonly formattedCustomerData: (customer: SelectionOnCustomer) => ITagInfo = customer => ({
    customer_id: customer.id,
    customer_uuid: customer.uuid,
    customer_email: customer.email,
    income_type: customer.income && customer.income.type || '',
  })

  private readonly formattedCustomerApplicationData: (app: SelectionOnCustomerApplication | null) => ITagInfo = app => {
    if (!app) {
      return {};
    }

    return {
      application_id: app.id || '',
      application_owner: app.applicationOwner || '',
      application_source: app.source || '',
      application_uuid: app.uuid || '',
      product_type: app.productType || '',
      lead_id: app.lead?.id || '',
      lead_source: app.lead?.source || '',
      lead_type: !!app.lead && this.getLeadType(app.lead) || '',
      referral_url: app.referrerUrl || '',
      status: app.status || '',
      loan_purpose: app.purpose || '',
      promotion_codes: app.promotionCode || '',
    };
}

  private readonly formattedProductData: (
    product: FragmentSelectionOnCreditCardAccount | FragmentSelectionOnLoan | null
  ) => ITagInfo = product => {
    if (!product) {
      return {};
    }

    return {
      // CC data
      annual_fee: isCreditCard(product) && product.annualMembershipFee?.amount || '',
      credit_limit_amount: isCreditCard(product) && product.creditLimit?.amount || '',

      // Loan data
      loan_amount: isLoan(product) &&
        getAmountValue({ amount: product.amount, amountWithFees: product.amountWithOtherOriginationFees }) ||
        '',
      loan_apr: isLoan(product) && product.calculatedApr || '',
      loan_id: isLoan(product) && product.id || '',
      loan_status: isLoan(product) && product.status || '',
      loan_term: isLoan(product) && product.term || '',
    };
  }

  private readonly getLeadType: (lead: SelectionOnLead) => string = lead => {
    if (lead.isPrefill) {
      return 'Prefill';
    } else if (lead.isPostLead) {
      return 'Post';
    } else {
      return 'Non-Post';
    }
  }

  private sendServerSideAnalyticsEvent: (
    appData: ITagInfo, isInVerification: boolean, pageSpecificTags?: ITagInfo
  ) => void = (appData, isInVerification, pageSpecificTags) => {
    if (isInVerification && appData.application_uuid) {
      const pageName = this.serverSidePageName(pageSpecificTags);

      this.sendServerSideEventMutation({
        input: {
          customerApplicationUUID: String(appData.application_uuid),
          verificationsPageName: pageName
        }
      });
    }
  };

  private serverSidePageName: (pageSpecificTags?: ITagInfo) => string = pageSpecificTags => {
    if (pageSpecificTags?.page_name) {
      return String(pageSpecificTags.page_name);
    } else if (pageSpecificTags?.mitek_page_name) {
      return `Mitek ${String(pageSpecificTags.mitek_page_name)}`;
    } else {
      return 'unknown';
    }
  };

  private sendServerSideEventMutation: (input: VerificationsAnalyticsEventInput) => Promise<FetchResult> = input => (
    client.mutate({ mutation: VerificationsAnalyticsEvent, variables: input })
  );
}

export default AvantTealium;
